import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@striker/ui-shell';
import { apiFetch } from '../auth/session';

interface ReviewItem {
  id: number;
  end_user_id: number;
  store_id: number;
  reservation_id: number;
  rating_food: number;
  rating_service: number;
  rating_ambience: number;
  rating_overall: number;
  comment: string | null;
  xp_awarded: number;
  created_at: string;
  end_user_display_name?: string;
  hidden: boolean;
  hidden_reason: string | null;
}

interface ReviewListResponse {
  reviews: ReviewItem[];
  total: number;
}

function Stars({ value }: { value: number }) {
  return (
    <span className="sk-stars-display">
      {'★'.repeat(value)}
      {'☆'.repeat(5 - value)}
    </span>
  );
}

export function MasterReviewsPage() {
  const [reviews, setReviews] = useState<ReviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterStoreId, setFilterStoreId] = useState('');
  const [hiddenOnly, setHiddenOnly] = useState(false);
  const [actionMsg, setActionMsg] = useState('');
  const [hideReason, setHideReason] = useState('');
  const [pendingHideId, setPendingHideId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterStoreId) params.set('storeId', filterStoreId);
      if (hiddenOnly) params.set('hiddenOnly', '1');
      params.set('limit', '50');
      const res = await apiFetch(`/api/v1/master/reviews?${params.toString()}`);
      if (!res.ok) throw new Error('Failed to load reviews');
      const data: ReviewListResponse = await res.json();
      setReviews(data.reviews || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [filterStoreId, hiddenOnly]);

  useEffect(() => {
    load();
  }, [load]);

  const doHide = async (id: number) => {
    const reason = hideReason || 'moderation';
    try {
      const res = await apiFetch(`/api/v1/master/reviews/${id}/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (res.ok) {
        setActionMsg(`Recensione #${id} nascosta`);
        setPendingHideId(null);
        setHideReason('');
        load();
      }
    } catch {
      setActionMsg('Errore nella modifica');
    }
  };

  const doUnhide = async (id: number) => {
    try {
      const res = await apiFetch(`/api/v1/master/reviews/${id}/unhide`, {
        method: 'POST',
      });
      if (res.ok) {
        setActionMsg(`Recensione #${id} ripristinata`);
        load();
      }
    } catch {
      setActionMsg('Errore nel ripristino');
    }
  };

  const sendReminders = async () => {
    if (!confirm('Inviare SMS di promemoria recensione per le prenotazioni completate di ieri?')) return;
    try {
      const res = await apiFetch('/api/v1/master/reviews/send-reminders', { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setActionMsg(`SMS inviati: ${data.sent} | Skipped: ${data.skipped} | Errori: ${data.errors}`);
      }
    } catch {
      setActionMsg('Errore invio SMS');
    }
  };

  return (
    <section className="sk-mc-page">
      <PageHeader
        title="Recensioni — Moderation"
        subtitle="Gestione recensioni verificate. Solo prenotazioni completed possono essere recensite."
      />

      {actionMsg && <p className="sk-success">{actionMsg}</p>}
      {error && <p className="sk-error">{error}</p>}

      <div className="sk-reviews-toolbar">
        <input
          type="text"
          placeholder="Filtra per store ID…"
          value={filterStoreId}
          onChange={(e) => setFilterStoreId(e.target.value)}
          className="sk-input"
          style={{ width: '120px' }}
        />
        <label className="sk-checkbox-label">
          <input
            type="checkbox"
            checked={hiddenOnly}
            onChange={(e) => setHiddenOnly(e.target.checked)}
          />
          Solo nascoste
        </label>
        <button onClick={load} className="sk-btn sk-btn--primary">
          Aggiorna
        </button>
        <button onClick={sendReminders} className="sk-btn sk-btn--info">
          Invia promemoria SMS
        </button>
      </div>

      {loading && <p className="sk-muted">Caricamento…</p>}

      {!loading && reviews.length === 0 && (
        <p className="sk-muted">Nessuna recensione trovata.</p>
      )}

      {reviews.length > 0 && (
        <table className="sk-table sk-reviews-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Utente</th>
              <th>Store</th>
              <th>Cibo</th>
              <th>Servizio</th>
              <th>Ambiente</th>
              <th>Overall</th>
              <th>Commento</th>
              <th>Data</th>
              <th>Stato</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
            {reviews.map((r) => (
              <tr key={r.id} className={r.hidden ? 'sk-row--hidden' : ''}>
                <td>{r.id}</td>
                <td>{r.end_user_display_name || `#${r.end_user_id}`}</td>
                <td>#{r.store_id}</td>
                <td><Stars value={r.rating_food} /></td>
                <td><Stars value={r.rating_service} /></td>
                <td><Stars value={r.rating_ambience} /></td>
                <td><strong>{r.rating_overall.toFixed(1)}</strong></td>
                <td className="sk-reviews-comment">{r.comment || '—'}</td>
                <td>{new Date(r.created_at).toLocaleDateString('it-IT')}</td>
                <td>
                  {r.hidden ? (
                    <span className="sk-badge sk-badge--red">NASCOSTA{r.hidden_reason ? `: ${r.hidden_reason}` : ''}</span>
                  ) : (
                    <span className="sk-badge sk-badge--green">Visibile</span>
                  )}
                </td>
                <td>
                  {r.hidden ? (
                    <button onClick={() => doUnhide(r.id)} className="sk-btn sk-btn--small sk-btn--primary">
                      Ripristina
                    </button>
                  ) : (
                    <button
                      onClick={() => setPendingHideId(r.id)}
                      className="sk-btn sk-btn--small sk-btn--warning"
                    >
                      Nascondi
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pendingHideId && (
        <div className="sk-modal-overlay" onClick={() => setPendingHideId(null)}>
          <div className="sk-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Nascondi recensione #{pendingHideId}</h3>
            <p className="sk-muted">La recensione sarà nascosta dalle card pubbliche. Le stats dello store verranno ricalcolate.</p>
            <input
              type="text"
              placeholder="Motivo (opzionale)…"
              value={hideReason}
              onChange={(e) => setHideReason(e.target.value)}
              className="sk-input"
            />
            <div className="sk-modal__actions">
              <button onClick={() => doHide(pendingHideId)} className="sk-btn sk-btn--warning">
                Conferma nascondi
              </button>
              <button onClick={() => setPendingHideId(null)} className="sk-btn sk-btn--neutral">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="sk-muted sk-muted--small">{total} recensioni totali</p>
    </section>
  );
}
